import type { DirectoryClinicDto } from "@clinic/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { useT } from "../i18n";
import { usePageMeta } from "../seo";
import { SiteFooter, SiteHeader } from "../site";
import { Alert, Card, Field, Spinner } from "../ui";

/** Public clinic directory (CB-23): clinics that opted in, searchable. */
export function DirectoryPage() {
  const { t, tError } = useT();
  usePageMeta("directory");
  const [q, setQ] = useState("");
  const clinics = useQuery({
    queryKey: ["directory", q],
    queryFn: () => api<{ items: DirectoryClinicDto[] }>("GET", `/public/clinics?q=${encodeURIComponent(q)}&limit=50`),
  });
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 space-y-4 px-4 pt-4 pb-16">
        <div>
          <h1 className="text-2xl font-semibold">{t("directory.title")}</h1>
          <p className="mt-1 text-slate-600">{t("directory.subtitle")}</p>
        </div>
        <Field label={t("directory.search")} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {clinics.isPending ? (
          <div className="flex justify-center p-6 text-brand-700">
            <Spinner />
          </div>
        ) : clinics.isError ? (
          <Alert>{tError(clinics.error)}</Alert>
        ) : clinics.data.items.length === 0 ? (
          <p className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-600">{t("directory.empty")}</p>
        ) : (
          <ul className="grid gap-3">
            {clinics.data.items.map((c) => (
              <li key={c.slug}>
                <Card className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    {c.address ? <p className="text-sm text-slate-600">{c.address}</p> : null}
                    {c.description ? <p className="mt-1 text-sm text-slate-500">{c.description}</p> : null}
                  </div>
                  <Link
                    to={`/book/${c.slug}`}
                    className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
                    aria-label={`${t("directory.book")}: ${c.name}`}
                  >
                    {t("directory.book")}
                  </Link>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
